// Date-of-birth rules for the driver application form.
// Behavioural mirror of api/src/validations/dob.ts. Keep the two answering identically.

import dayjs, { type Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
dayjs.extend(customParseFormat)

export const MIN_DRIVER_AGE_YEARS = 18
export const MAX_DRIVER_AGE_YEARS = 100

export const DOB_INVALID_MESSAGE = 'This is not a valid date'
export const DOB_FUTURE_MESSAGE = 'You must select a date in the past'
export const DOB_TOO_YOUNG_MESSAGE = 'You must be at least 18 years old'
export const DOB_TOO_OLD_MESSAGE =
  'Please select a date within the last 100 years'

/**
 * UTC calendar day for a Date or a date string.
 * MM/DD/YYYY and YYYY-MM-DD are read as that calendar day. Strict parsing
 * rejects impossible days such as February 31 instead of rolling them into
 * the next month.
 */
export const parseDateOfBirth = (value: unknown): Dayjs | null => {
  if (value instanceof Date) {
    const parsed = dayjs.utc(value)
    return parsed.isValid() ? parsed.startOf('day') : null
  }

  if (typeof value !== 'string') return null
  const trimmed = value.trim()

  const us = dayjs.utc(trimmed, 'MM/DD/YYYY', true)
  if (us.isValid()) return us

  if (!dayjs.utc(trimmed.slice(0, 10), 'YYYY-MM-DD', true).isValid()) {
    return null
  }
  if (trimmed.length > 10 && trimmed[10] !== 'T') return null
  const parsed = dayjs.utc(trimmed)
  return parsed.isValid() ? parsed.startOf('day') : null
}

export const dateOfBirthError = (
  value: unknown,
  now: Date = new Date()
): string | null => {
  const dob = parseDateOfBirth(value)
  if (!dob) return DOB_INVALID_MESSAGE

  const today = dayjs.utc(now)
  if (dob.isAfter(today, 'day')) return DOB_FUTURE_MESSAGE
  if (dob.isAfter(today.subtract(MIN_DRIVER_AGE_YEARS, 'year'), 'day')) {
    return DOB_TOO_YOUNG_MESSAGE
  }
  if (dob.isBefore(today.subtract(MAX_DRIVER_AGE_YEARS, 'year'), 'day')) {
    return DOB_TOO_OLD_MESSAGE
  }
  return null
}

export const isSameDateOfBirth = (left: unknown, right: unknown): boolean => {
  const parsedLeft = parseDateOfBirth(left)
  const parsedRight = parseDateOfBirth(right)
  if (!parsedLeft || !parsedRight) return false
  return parsedLeft.isSame(parsedRight, 'day')
}
